import Backbone from "backbone";
import $ from "jquery";
import * as preact from "preact";
import * as _ from "underscore";

import * as mustcache from "mustcache";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import * as utils from "shared/utils";
import * as textToSpeech from "textToSpeech";
import EditCardView from "views/EditCardView";

const allNbsp = new RegExp(String.fromCharCode(160), "g");

const RemoteWordListView = Backbone.View.extend({
  events: {
    "click .checkboxLabel": "clickCheckboxLabel",
    "click .wordRow": "clickWordRow",
    "click .delete": "clickDelete",
    "click .audioButton": "clickAudio",
    "click .favorite": "clickFavorite",
    'change input[name="wordGroup"]': "wordGroupChange",
    "change .sortBy": "changeSortBy",
    "click .externalLink": "clickExternalLink",
  },
  clickExternalLink: function (event) {
    event.stopPropagation();
  },
  setFilter: function (filter) {
    this.options.from = 1;
    this.options.filter = filter;
    this.lazyRender();
  },
  /** @param {UserWord & {wordWithContext: string, source: {title: string, href: string}}} word */
  _calculateDisplayContext: function (word) {
    const bookID = this.model.searchQuery.get("bookIDs");

    /** @type {UserWord["contexts"][0]} */
    let context;

    _.every(word.contexts, (_context) => {
      if (!_context || !_context.text) {
        return true;
      }
      if (bookID && (!_context.bookID || bookID !== _context.bookID._id)) {
        return true;
      }
      context = _context;
      return false;
    });

    if (!context) {
      return;
    }

    const contextText = context.text;
    let contextHtml = contextText.replace(
      new RegExp("(\\W|^)(" + utils.regexpEscape(word.word) + ")(\\W|$)", "i"),
      "$1<strong>$2</strong>$3"
    );

    if (/<strong>/.test(contextHtml)) {
      const indexOfStart = contextHtml.indexOf("<strong>");
      if (indexOfStart > 10) {
        contextHtml = "…" + contextHtml.substring(indexOfStart - 10);
      }

      const indexOfEnd = contextHtml.indexOf("</strong>");
      if (contextHtml.length - indexOfEnd > 40) {
        contextHtml = utils.elide(contextHtml, indexOfEnd + 40);
      }

      word.wordWithContext = contextHtml.replace(allNbsp, " ");
    }

    if (context.bookID && context.bookID.title) {
      word.source = {
        title: utils.elide(context.bookID.title, 40),
        href: "/library/" + context.bookID._id,
      };
    } else if (context.URL) {
      word.source = {
        title: utils.elide(utils.getDomainFromUrl(context.URL), 40),
        href: context.URL,
      };
    } else if (context.youTubeID) {
      word.source = {
        title: "YouTube",
        href: "https://www.youtube.com/watch?v=" + context.youTubeID,
      };
    }
  },

  getTemplateData: function () {
    const that = this;
    const words = this.model.toJSON();
    const languageInfo =
      languageMap.map[this.options.models.user.get("currentLearningLanguage")];

    const wordsToShow = words;

    _.each(wordsToShow, (word) => {
      that._calculateDisplayContext(word);
      word.date = dateUtils.format(
        dateUtils.dateFromObjectId(word._id),
        "%YYYY/%MM/%DD"
      );
      word.displayTranslations = this.model.get(word._id).formatTranslations();
    });

    const templateData: any = {
      languageString: languageInfo.name,
      words: wordsToShow,
      from: this.model.searchQuery.get("from") + 1,
      to: Math.min(
        this.model.searchQuery.get("from") +
          this.model.searchQuery.get("limit"),
        this.model.totalCount
      ),
      totalCount: this.model.totalCount,
      wordsExist: wordsToShow.length > 0,
      tts: textToSpeech.languageSupported(
        this.options.models.user.get("currentLearningLanguage")
      ),
      noWordsMessage: this.options.noWordsMessage,
    };

    if (this.options.wordGroup) {
      const title = this.options.wordGroup.replace(/([A-Z])/g, " $1");
      templateData.title =
        "Words " + title[0].toUpperCase() + title.substring(1);
    }
    if (this.options.wordGroup === "notStarted") {
      templateData.frequencies = languageInfo.frequencies;
    }
    if ("showCancelButton" in this.options) {
      templateData.showCancelButton = this.options.showCancelButton;
    } else {
      templateData.showCancelButton = true;
    }

    return templateData;
  },
  initialize: function (options) {
    const that = this;

    this.wordsIncrement = 200;
    this.options = _.defaults(options || {}, {
      noWordsMessage: "There are no words here.",
      maxWords: 100,
      from: 1,
    });

    this.listenTo(this.model, "remove", (model) => {
      const row = that.$el.find(
        '.wordRow[data-userWordId="' +
          model.get("_id") +
          '"], ' +
          '.editCardPanelRow[data-userWordId="' +
          model.get("_id") +
          '"]'
      );
      row.remove();
    });
    this.listenTo(this.model, "add", () => {
      that.render();
    });
    this.listenTo(this.model, "change:word change:contexts", (model) => {
      const word = model.toJSON();
      const element = that.$el.find("#word-" + model.get("_id"));
      that._calculateDisplayContext(word);
      if (word.wordWithContext) {
        element.html(word.wordWithContext);
      } else {
        element.text(word.word);
      }
    });
    this.listenTo(
      this.model,
      "change:translation change:alternativeTranslations",
      (model) => {
        that.$el
          .find("#translation-" + model.get("_id"))
          .text(model.formatTranslations());
      }
    );
    this.listenTo(this.model, "reset", () => {
      that.render();
    });
    this.listenTo(this.model, "sort", () => {
      that.render();
    });

    this.listenTo(this.model.selectionModel, "change:wholePage", () => {
      if (that.model.selectionModel.get("wholePage")) {
        that.$el.find('input[type="checkbox"]').prop("checked", true);
      } else {
        that.$el.find('input[type="checkbox"]').prop("checked", false);
      }
    });
    this.listenTo(this.model.selectionModel, "change:itemIDs", () => {
      const selectedUserWordIDs = that.model.selectionModel.get("itemIDs");

      that.$el.find(".wordRow").each((index, row) => {
        const $row = $(row);
        const selected = _.contains(
          selectedUserWordIDs,
          $row.attr("data-userWordID")
        );

        if (selected) {
          $row.addClass("selected");
          $row.find('input[type="checkbox"]').prop("checked", true);
        } else {
          $row.removeClass("selected");
          $row.find('input[type="checkbox"]').prop("checked", false);
        }
      });
    });

    this.lazyRender = _.debounce(() => {
      that.render();
    }, 200);

    this.render();
  },
  render: function () {
    this.$el.html($(mustcache.render("wordList", this.getTemplateData())));
    this.$el
      .find(
        '.sortBy option[data-sortBy="' +
          this.model.searchQuery.get("sortBy") +
          '"]'
      )
      .prop("selected", true);

    this.delegateEvents(this.events);
  },
  showExtraWords: function () {
    this.options.maxWords += this.wordsIncrement;
    this.render();
  },
  clickCheckboxLabel: function (event) {
    event.stopPropagation();
    event.preventDefault();

    const $label = $(event.currentTarget);

    this._clickCheckbox($("#" + $label.attr("for")), event.shiftKey);
  },
  _clickCheckbox: function (thisCheckbox, shiftKey) {
    const that = this;

    const userWordID = thisCheckbox.attr("data-userWordID");
    let checked = !thisCheckbox.prop("checked");

    if (shiftKey && this.prevCheckbox) {
      // select all items between the original and this one
      var range = this.$el.find('input[type="checkbox"]');

      const index1 = range.index(thisCheckbox);
      const index2 = range.index(this.prevCheckbox);

      range = range.slice(
        Math.min(index1, index2),
        Math.max(index1, index2) + 1
      );

      checked = this.prevCheckboxChecked;
      range = _.map(range, (el) => $(el).attr("data-userWordID"));
    }

    this.prevCheckbox = thisCheckbox;
    this.prevCheckboxChecked = checked;

    setTimeout(() => {
      if (checked) {
        if (range) {
          that.model.selectionModel.addItems(range);
        } else {
          that.model.selectionModel.addItem(userWordID);
        }
      } else {
        if (range) {
          that.model.selectionModel.removeItems(range);
        } else {
          that.model.selectionModel.removeItem(userWordID);
        }
      }
    }, 0);
  },
  clickAudio: function (event) {
    const $this = $(event.target);
    const thisWordId = $this.closest("tr").attr("data-userWordId");
    const userWord = this.model.get(thisWordId);

    $this.removeClass("icon-volume-down");
    $this.addClass("icon-spinner icon-spin");
    textToSpeech.play(userWord.get("language"), userWord.get("word"), () => {
      $this.addClass("icon-volume-down");
      $this.removeClass("icon-spinner icon-spin");
    });

    event.stopPropagation();
  },
  clickFavorite: function (event) {
    const $this = $(event.target);
    const thisWordId = $this.closest("tr").attr("data-userWordId");
    const userWord = this.model.get(thisWordId);

    if (userWord.get("favorite")) {
      $this.removeClass("selected");
      userWord.save({ favorite: false }, { patch: true });
    } else {
      $this.addClass("selected");
      userWord.save({ favorite: true }, { patch: true });
    }

    event.stopPropagation();
  },
  clickDelete: function () {
    const that = this;
    const checkedElements = this.$el.find('input[type="checkbox"]:checked');
    const checkedUserWords = [];

    checkedElements.each((index, element) => {
      checkedUserWords.push(that.model.get($(element).attr("data-userWordId")));
    });

    if (checkedUserWords.length === 0) {
      console.error("no selected elements");
    } else {
      const wordsString =
        checkedUserWords.length === 1
          ? "word"
          : checkedUserWords.length + " words";
      if (
        confirm(
          "Are you sure you want to delete the selected " + wordsString + "?"
        )
      ) {
        _.each(checkedUserWords, (userWord) => {
          if (userWord.destroy) {
            userWord.destroy();
          } else {
            console.error("No destroy method");
          }
        });
      }
    }
  },
  clickWordRow: function (event) {
    const $target = $(event.target).closest(".wordRow");

    const id = $target.attr("data-userWordId");
    const userWord = this.model.get(id);

    if (!userWord) {
      throw "user word not found";
    }

    if (this.editWordPanel) {
      this.editWordPanel.destroy();
    }

    const existingRow = $(".editCardPanelRow");

    if (existingRow.length > 0 && existingRow.attr("data-userWordId") === id) {
      existingRow.remove();

      if (this.editWordPanelElement) {
        preact.render(null, this.editWordPanelElement);
        this.editWordPanelElement = undefined;
      }
      return;
    }
    existingRow.remove();

    // cspell: disable-next-line
    const cell = $('<td colspan="6"/>');
    $target.after(
      $('<tr class="editCardPanelRow" data-userWordId="' + id + '"/>').append(
        cell
      )
    );

    cell.empty();
    preact.render(
      <EditCardView
        userWord={userWord}
        options={{}}
        phrase={/ /.test(userWord.get("word"))}
      />,
      cell[0]
    );
    this.editWordPanelElement = cell[0];
  },
  setNoWordsMessage: function (message) {
    this.$el.find(".noWordsMessage").text(message);
  },
  changeSortBy: function () {
    const sortBy = this.$el.find(".sortBy option:selected").attr("data-sortBy");
    this.model.fetch({
      data: {
        sortBy: sortBy,
      },
    });
  },
});

export default RemoteWordListView;
